import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { Box, Button, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core/styles';
import { Chat } from '@material-ui/icons';

import { competitiveSetBrandsAlgorithmCalculation } from '../../../redux/actions/Firebase';
import { makeURL, findLatestExistingBatchData } from '../../../helperFunctions';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';

import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';
import ChartCalculations from './chartCalculations';

import HeaderSelector from '../../../myComponents/headerSelector';
import Filters from '../../../myComponents/filters/Filters';
import PDFExportButton from '../../../myComponents/PDFExportButton';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';
import ChatBot from '../../../myComponents/chatBot';

const useStyles = makeStyles(theme => ({
  chatButton: {
    display: 'flex',
    margin: '10px 0',
    padding: '7px 25px',
    borderRadius: '5px',
    alignItems: 'center',
    backgroundColor: '#19989B',

    '&.MuiButton-root:hover.Mui-disabled': {
      backgroundColor: '#19989B',
      color: '#fff !important',
    },

    '&.MuiButton-root.Mui-disabled': {
      backgroundColor: '#19989B',
      color: '#fff !important',
    },

    '&:hover': {
      backgroundColor: '#19878a',
    },

    '@media (max-width: 768px)': {
      padding: '5px 20px',
    },

    '@media (min-width: 1300px)': {
      margin: '15px 0',
      padding: '5px 25px',
    },
  },
  chatButtonText: {
    color: 'white',
    fontSize: '12px',
    margin: 0,

    '@media (max-width: 768px)': {
      fontSize: '10px',
    },

    '@media (min-width: 1300px)': {
      fontSize: '15px',
    },
  },
  chatIcon: {
    fontSize: '20px',
    color: 'white',
    marginRight: '7px',

    '@media (max-width: 768px)': {
      fontSize: '13px',
    },
  },
  headerSelectorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gridColumnGap: '40px',

    '@media (max-width: 768px)': {
      gridColumnGap: '30px',
    },
  },
}));

const CompetitiveSetBrandsDashboard = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const locationState = location.state;

  const dispatch = useDispatch();
  const appData = useSelector(({ data }) => data);
  const [brandPickedId, setBrandPickedId] = useState(null);
  const PDFContentRef = useRef(null);
  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);
  const [chosenFilters, setChosenFilters] = useState(null);

  const [chatOpened, setChatOpened] = useState(false);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [useEffectShouldTrigger, setUseEffectShouldTrigger] = useState(true);

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: false,
    results: null,
  });

  const [pickedBrand, setPickedBrand] = useState(null);

  useEffect(() => {
    if (!appData.loading && useEffectShouldTrigger) {
      setUseEffectShouldTrigger(false);
      if (appData.error) {
        showToastWarn();
      } else {
        if (!id) {
          history.push(makeURL('/competitive-set-brands/all'));
        } else {
          // Batch that was chosen in view all, batch that was chosen in dashboard, fallback to default
          const suggestedBatchCountry = locationState?.batchCountry || pickedBatchCountry || DEFAULT_BATCH_COUNTRY;

          const pickedBrand =
            !appData.batchBrands || !appData.batchBrands[suggestedBatchCountry]
              ? false
              : appData.batchBrands[suggestedBatchCountry].find(e => e.id === id);
          if (!pickedBrand) return history.push(makeURL('/competitive-set-brands/all'));

          if (!pickedBatchData) {
            let pickedBatchToSet;

            if (locationState?.batchData) pickedBatchToSet = locationState.batchData;
            else {
              const { latestQuarter, latestYear } = findLatestExistingBatchData(
                appData.existingBatchData[suggestedBatchCountry],
              );
              pickedBatchToSet = {
                quarter: [latestQuarter],
                year: latestYear,
              };
            }

            setPickedBatchData(pickedBatchToSet);
            setPickedBatchCountry(suggestedBatchCountry);

            brandChangeHandler(pickedBrand, pickedBatchToSet, suggestedBatchCountry);
          } else {
            brandChangeHandler(pickedBrand, pickedBatchData, pickedBatchCountry);
          }

          setPageIsLoading(false);
        }
      }
    }
  }, [appData.loading, id]);

  useEffect(() => {
    if (brandPickedId) {
      history.push(makeURL(`/competitive-set-brands/${brandPickedId}`));
      setUseEffectShouldTrigger(true);
    }
  }, [brandPickedId]);

  const calculateHandler = (userPickedBrand, filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    competitiveSetBrandsAlgorithmCalculation(
      {
        brandName: userPickedBrand.name,
        filters: filters,
        batches: newPickedBatchData,
        batchCountry: newPickedBatchCountry,
      },
      dispatch,
    )
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn();
      });
  };

  const brandChangeHandler = (userPickedBrand, newPickedBatchData, newPickedBatchCountry) => {
    setPickedBrand(userPickedBrand);
    calculateHandler(userPickedBrand, chosenFilters, newPickedBatchData, newPickedBatchCountry);
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    calculateHandler(pickedBrand, preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run brand algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBrand, chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestBatchYear = Object.keys(appData.existingBatchData[value])
      .sort()
      .pop();
    const quarters = appData.existingBatchData[value][newestBatchYear].sort((a, b) => a.localeCompare(b));
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: newestBatchYear,
    };

    setPickedBatchData(batchInfoToSet);
    setPickedBatchCountry(value);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBrand, chosenFilters, batchInfoToSet, value);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.existingBatchData[pickedBatchCountry][value].sort((a, b) => a.localeCompare(b));
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: value,
    };

    setPickedBatchData(batchInfoToSet);

    //Run buyer persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBrand, chosenFilters, batchInfoToSet, pickedBatchCountry);
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong, please try again later.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <PageContainer>
      {pageIsLoading || calculations.loading ? <PageLoader dashboardCalculation /> : null}
      {calculations.results ? (
        <div ref={PDFContentRef}>
          <UserDropdownHeader />

          {pickedBrand && (
            <ChatBot pickedPersona={pickedBrand} chatOpened={chatOpened} setChatOpened={setChatOpened} type="competitive" />
          )}

          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            padding="20px 20px 0px 20px"
            style={{ background: '#fff', gap: '30px' }}>
            <Box flex="1">
              <Box className={classes.headerSelectorContainer}>
                <HeaderSelector
                  selectedId={brandPickedId ? brandPickedId : id}
                  setPickedId={setBrandPickedId}
                  pickedBatchCountry={pickedBatchCountry}
                  data={appData}
                  sameIndustryData={calculations.results.companies_match}
                  type="competitive"
                  redirect={true}
                />
                <Button onClick={() => setChatOpened(!chatOpened)} className={classes.chatButton}>
                  <Chat className={classes.chatIcon} />
                  <Typography component="div" variant="button" className={classes.chatButtonText}>
                    CHAT
                  </Typography>
                </Button>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <BuyerBatchSelector
                calculationType={'batch'}
                onChangeCalculationType={() => {}}
                pickedBuyerPersona={null}
                onChangeBuyerPersona={() => {}}
                pickedBatchData={pickedBatchData}
                pickedBatchCountry={pickedBatchCountry}
                onChangeBatchCountry={onChangeBatchCountry}
                onChangeBatchQuarter={onChangeBatchQuarter}
                onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
                onChangeBatchYear={onChangeBatchYear}
                type="competitive"
                withConsumerHeading
                selectedCompetitiveId={brandPickedId ? brandPickedId : id}
              />
            </Box>
          </Box>

          <Box>
            <Filters calculateHandler={onFiltersSubmit} themeColor="green" pickedBatchCountry={pickedBatchCountry} pickedBatchData={pickedBatchData} />
            {/* <PDFExportButton contentRef={PDFContentRef} type="green" /> */}
            {calculations.results.no_data ? (
              <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
                <Typography
                  variant="h6"
                  color="textSecondary"
                  style={{
                    fontSize: '20px',
                    margin: '0',
                    padding: '0',
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}>
                  No data, choose other "Consumer Settings" combination.
                </Typography>
              </Box>
            ) : (
              <ChartCalculations data={calculations.results} name={pickedBrand.name} />
            )}
          </Box>
        </div>
      ) : null}
      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default CompetitiveSetBrandsDashboard;
